import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ToastContainer } from 'react-toastify';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TimezoneProvider } from './context/TimezoneContext';
import { useTheme } from './context/ThemeContext';
import { useRole } from './hooks/useRole';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { FormBuilderPage } from './pages/FormBuilderPage';
import { FormsPage } from './pages/FormsPage';
import { FormSubmissionsPage } from './pages/FormSubmissionsPage';
const ExcelExportConfigPage = React.lazy(() => import('./pages/ExcelExportConfigPage').then(m => ({ default: m.ExcelExportConfigPage })));
const ExcelImportPage = React.lazy(() => import('./pages/ExcelImportPage').then(m => ({ default: m.ExcelImportPage })));
import { CreateSubmissionPage } from './pages/CreateSubmissionPage';
import { SubformSubmissionPage } from './pages/SubformSubmissionPage';
import { SubmissionDetailPage } from './pages/SubmissionDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { OrganizationDetailPage } from './pages/OrganizationDetailPage';
import { DemoStudioPage } from './pages/DemoStudioPage';
import { DemoEditorPage } from './pages/DemoEditorPage';
const DemoPageEditorPage = React.lazy(() => import('./pages/DemoPageEditorPage').then(m => ({ default: m.DemoPageEditorPage })));
import { FAQsPage } from './pages/FAQsPage';
import { CreateFaqPage } from './pages/CreateFaqPage';
import { HelpPage } from './pages/HelpPage';
import { PDFTemplateManagerPage } from './pages/PDFTemplateManagerPage';
import { StandaloneVizManagerPage } from './pages/StandaloneVizManagerPage';
const StandaloneVizEditorPage = React.lazy(() => import('./pages/StandaloneVizEditorPage').then(m => ({ default: m.StandaloneVizEditorPage })));
import { WorkOrdersPage } from './pages/WorkOrdersPage';
import { WorkOrderDetailPage } from './pages/WorkOrderDetailPage';
const WorkOrderReportConfigPage = React.lazy(() => import('./pages/WorkOrderReportConfigPage').then(m => ({ default: m.WorkOrderReportConfigPage })));
import { CustomersPage } from './pages/CustomersPage';
import { LocationsPage } from './pages/LocationsPage';
import { CreateWorkOrderPage } from './pages/CreateWorkOrderPage';
// Lazy-loaded pages with heavy dependencies (PDF designer, calendar, email designer, etc.)
const PDFTemplateDesignerPage = React.lazy(() => import('./pages/PDFTemplateDesignerPage').then(m => ({ default: m.PDFTemplateDesignerPage })));
const PDFTemplateTesterPage = React.lazy(() => import('./pages/PDFTemplateTesterPage').then(m => ({ default: m.PDFTemplateTesterPage })));
const ImageReportConfigPage = React.lazy(() => import('./pages/ImageReportConfigPage').then(m => ({ default: m.ImageReportConfigPage })));
const CalendarPage = React.lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
import { LicensePlansPage } from './pages/LicensePlansPage';
import { CreateLicensePlanPage } from './pages/CreateLicensePlanPage';
import { LicenseManagementPage } from './pages/LicenseManagementPage';
import { OrganizationLicenseDetailPage } from './pages/OrganizationLicenseDetailPage';
import { FileManagerPage } from './pages/FileManagerPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { EmailTemplatesPage } from './pages/EmailTemplatesPage';
const EmailTemplateDesignerPage = React.lazy(() => import('./pages/EmailTemplateDesignerPage').then(m => ({ default: m.EmailTemplateDesignerPage })));
import { SmsTemplatesPage } from './pages/SmsTemplatesPage';
import { GeneratedReportsPage } from './pages/GeneratedReportsPage';
import { DeveloperToolsPage } from './pages/DeveloperToolsPage';
import { TasksPage } from './pages/TasksPage';
import { CreateTaskPage } from './pages/CreateTaskPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { SentryIssueDetailPage } from './pages/SentryIssueDetailPage';
import { WebhookEndpointPage } from './pages/WebhookEndpointPage';
import { InboundWebhookEndpointPage } from './pages/InboundWebhookEndpointPage';
import { LabReportsPage } from './pages/LabReportsPage';
import { LabReportUploadPage } from './pages/LabReportUploadPage';
import { LabReportDetailPage } from './pages/LabReportDetailPage';
import { LabReportTemplatesPage } from './pages/LabReportTemplatesPage';
import { LabDocumentFormatsPage } from './pages/LabDocumentFormatsPage';
const LabFormatCalibrationPage = React.lazy(() => import('./pages/LabFormatCalibrationPage').then(m => ({ default: m.LabFormatCalibrationPage })));
import { LabFormatDetailPage } from './pages/LabFormatDetailPage';
import { DatabaseBackupPage } from './pages/DatabaseBackupPage';
import { KnowledgeBasePage } from './pages/KnowledgeBasePage';
import styled, { createGlobalStyle, ThemeProvider as StyledThemeProvider } from 'styled-components';
import 'react-toastify/dist/ReactToastify.css';
import { DEV_TOOLS_ENABLED } from './utils/devTools';
import { initBugReporter } from './utils/bugReporterContext';

// Initialize QA bug-reporter capture exactly once (no-op in production).
if (DEV_TOOLS_ENABLED) initBugReporter();

// Create Sentry-wrapped Routes for navigation tracking
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

// Global styles
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body, #root {
    height: 100%;
    overflow: hidden;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  }

  body {
    background-color: ${props => props.theme?.background};
    color: ${props => props.theme?.text};
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  button, input, textarea {
    font-family: inherit;
  }

  /* Custom toast styles to match theme */
  .Toastify__toast {
    border-radius: 8px;
  }

  .Toastify__toast--success {
    background-color: #28a745;
    color: #fff;
  }

  .Toastify__toast--error {
    background-color: #dc3545;
    color: #fff;
  }

  .Toastify__toast--info {
    background-color: #232562;
    color: #fff;
  }

  .Toastify__toast--warning {
    background-color: #ffc107;
    color: #000;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme?.colors?.background};
  color: ${props => props.theme?.colors?.text};
  transition: all 0.2s ease;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: ${props => props.theme?.colors?.background};

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid ${props => props.theme?.colors?.border || '#dee2e6'};
    border-top-color: ${props => props.theme?.colors?.tint || '#59caee'};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const NoticeScreen = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 32px;
  background-color: ${props => props.theme?.colors?.background};
`;

const NoticeCard = styled.div`
  max-width: 440px;
  padding: 40px;
  text-align: center;
  border: 1px solid ${props => props.theme?.colors?.border || '#e2e8f0'};
  border-radius: 12px;
  background-color: ${props => props.theme?.colors?.backgroundSecondary};
  color: ${props => props.theme?.colors?.text};
`;

const NoticeButton = styled.button`
  margin-top: 24px;
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background-color: ${props => props.theme?.colors?.tint || '#59caee'};
  color: #fff;
  font-weight: 600;
  cursor: pointer;
`;

// Shown to a signed-in user whose account has not been assigned to an organization.
// Without an organization they have no scoped data, and shared dashboards would
// otherwise surface other organizations' data.
const NoOrganizationNotice = ({ theme }) => {
  const { logout } = useAuth();
  return (
    <NoticeScreen theme={theme}>
      <NoticeCard theme={theme}>
        <h2>Account not assigned</h2>
        <p>Your account isn't assigned to an organization yet. Please contact your administrator to get access.</p>
        <NoticeButton theme={theme} onClick={() => logout()}>Sign out</NoticeButton>
      </NoticeCard>
    </NoticeScreen>
  );
};

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { isSuperAdmin, getOrganizationId } = useRole();
  const theme = useTheme();

  if (loading) {
    return <LoadingSpinner theme={theme} />;
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // A user not yet assigned to an organization has no scoped data — block them so
  // shared dashboards don't surface other organizations' data. SuperAdmins operate
  // above any single organization and are exempt.
  if (!isSuperAdmin() && !getOrganizationId()) {
    return <NoOrganizationNotice theme={theme} />;
  }

  // Keying on the user id forces React to unmount/remount the page subtree when the
  // dev account switcher logs in as a different user, so every page's mount-time
  // data fetches re-run instead of showing the previous account's stale data.
  return <React.Fragment key={user?.id}>{children}</React.Fragment>;
};

// Admin-only route component (OrgAdmin or SuperAdmin)
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { isAdmin } = useRole();
  const theme = useTheme();

  if (loading) {
    return <LoadingSpinner theme={theme} />;
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin()) return <Navigate to="/dashboard" replace />;
  return <React.Fragment key={user?.id}>{children}</React.Fragment>;
};

// Suspense fallback for lazy-loaded routes
const LazyFallback = () => {
  const theme = useTheme();
  return <LoadingSpinner theme={theme} />;
};

// Main app content
const AppContent = () => {
  const theme = useTheme();

  return (
    <StyledThemeProvider theme={theme}>
      <AppContainer theme={theme}>
        <GlobalStyle theme={theme.colors} />
        <SentryRoutes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms"
          element={
            <ProtectedRoute>
              <FormsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/repeated-forms"
          element={<Navigate to="/forms" replace />}
        />
        <Route
          path="/hidden-forms"
          element={<Navigate to="/forms" replace />}
        />
        <Route
          path="/form-builder"
          element={
            <ProtectedRoute>
              <FormBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/form-builder/:id"
          element={
            <ProtectedRoute>
              <FormBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/submissions"
          element={
            <ProtectedRoute>
              <FormSubmissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/submissions/export-excel"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <ExcelExportConfigPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/submissions/import-excel"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <ExcelImportPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/submissions/create/:sessionGuid?"
          element={
            <ProtectedRoute>
              <CreateSubmissionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/submissions/create/:sessionGuid/subform/:fieldId/:instanceIndex?"
          element={
            <ProtectedRoute>
              <SubformSubmissionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/submissions/:submissionId"
          element={
            <ProtectedRoute>
              <SubmissionDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/pdf-templates"
          element={
            <ProtectedRoute>
              <PDFTemplateManagerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/pdf-templates/designer/:templateId?"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <PDFTemplateDesignerPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/pdf-templates/test/:templateId"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <PDFTemplateTesterPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/pdf-templates/:templateId/image-report"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <ImageReportConfigPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/visualizations"
          element={
            <ProtectedRoute>
              <StandaloneVizManagerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forms/:formId/visualizations/:vizId"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <StandaloneVizEditorPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/profile" element={<Navigate to="/settings?tab=profile" replace />} />
        <Route
          path="/organizations"
          element={
            <ProtectedRoute>
              <OrganizationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizations/:id"
          element={
            <ProtectedRoute>
              <OrganizationDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/demo-studio"
          element={
            <ProtectedRoute>
              <DemoStudioPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/demo-studio/new"
          element={
            <ProtectedRoute>
              <DemoEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/demo-studio/:orgId/page"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <DemoPageEditorPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/demo-studio/:orgId"
          element={
            <ProtectedRoute>
              <DemoEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge-base"
          element={
            <ProtectedRoute>
              <KnowledgeBasePage />
            </ProtectedRoute>
          }
        />
        <Route path="/users" element={<Navigate to="/settings?tab=users" replace />} />
        <Route
          path="/faqs"
          element={
            <ProtectedRoute>
              <FAQsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/faqs/new"
          element={
            <ProtectedRoute>
              <CreateFaqPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/faqs/:id/edit"
          element={
            <ProtectedRoute>
              <CreateFaqPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <CalendarPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/work-orders"
          element={
            <ProtectedRoute>
              <WorkOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/work-orders/new"
          element={
            <ProtectedRoute>
              <CreateWorkOrderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/work-orders/:id/edit"
          element={
            <ProtectedRoute>
              <CreateWorkOrderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/work-orders/:id/report"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <WorkOrderReportConfigPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/work-orders/:id"
          element={
            <ProtectedRoute>
              <WorkOrderDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute>
              <CustomersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/locations"
          element={
            <ProtectedRoute>
              <LocationsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/my-certificates" element={<Navigate to="/settings?tab=certificates" replace />} />
        <Route path="/certificates" element={<Navigate to="/settings?tab=certificates&sub=types" replace />} />
        <Route
          path="/license-plans/new"
          element={
            <ProtectedRoute>
              <CreateLicensePlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/license-plans/:id/edit"
          element={
            <ProtectedRoute>
              <CreateLicensePlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/license-plans"
          element={
            <ProtectedRoute>
              <LicensePlansPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/license-management"
          element={
            <ProtectedRoute>
              <LicenseManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/license-management/:licenseId"
          element={
            <ProtectedRoute>
              <OrganizationLicenseDetailPage />
            </ProtectedRoute>
          }
        />
        <Route path="/license-status" element={<Navigate to="/settings?tab=license" replace />} />
        <Route
          path="/file-manager"
          element={
            <ProtectedRoute>
              <FileManagerPage />
            </ProtectedRoute>
          }
        />
        <Route path="/notification-events" element={<Navigate to="/settings?tab=configuration" replace />} />
        <Route
          path="/email-templates/designer/:id"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <EmailTemplateDesignerPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/email-templates"
          element={
            <ProtectedRoute>
              <EmailTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sms-templates"
          element={
            <ProtectedRoute>
              <SmsTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/generated-reports"
          element={
            <ProtectedRoute>
              <GeneratedReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/developer-tools"
          element={
            <ProtectedRoute>
              <DeveloperToolsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <TasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/new"
          element={
            <ProtectedRoute>
              <CreateTaskPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/:id/edit"
          element={
            <ProtectedRoute>
              <CreateTaskPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/:id"
          element={
            <ProtectedRoute>
              <TaskDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sentry-issues/:issueId"
          element={
            <ProtectedRoute>
              <SentryIssueDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/webhooks/outbound/new"
          element={
            <ProtectedRoute>
              <WebhookEndpointPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/webhooks/outbound/:id/edit"
          element={
            <ProtectedRoute>
              <WebhookEndpointPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/webhooks/inbound/new"
          element={
            <ProtectedRoute>
              <InboundWebhookEndpointPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/webhooks/inbound/:id/edit"
          element={
            <ProtectedRoute>
              <InboundWebhookEndpointPage />
            </ProtectedRoute>
          }
        />
        <Route path="/webhooks" element={<Navigate to="/settings?tab=integrations" replace />} />
        {/* Redirects for old webhook routes */}
        <Route path="/webhooks/new" element={<Navigate to="/webhooks/outbound/new" replace />} />
        <Route path="/inbound-webhooks/new" element={<Navigate to="/webhooks/inbound/new" replace />} />
        <Route path="/inbound-webhooks" element={<Navigate to="/settings?tab=integrations&sub=inbound" replace />} />
        <Route path="/audit-log" element={<Navigate to="/settings?tab=audit-log" replace />} />
        <Route
          path="/database-backups"
          element={
            <ProtectedRoute>
              <DatabaseBackupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lab-reports"
          element={
            <AdminRoute>
              <LabReportsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/lab-reports/upload"
          element={
            <AdminRoute>
              <LabReportUploadPage />
            </AdminRoute>
          }
        />
        <Route
          path="/lab-reports/:id"
          element={
            <AdminRoute>
              <LabReportDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/lab-report-templates"
          element={
            <AdminRoute>
              <LabReportTemplatesPage />
            </AdminRoute>
          }
        />
        <Route
          path="/lab-formats"
          element={
            <AdminRoute>
              <LabDocumentFormatsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/lab-formats/calibrate/:id?"
          element={
            <AdminRoute>
              <React.Suspense fallback={<LazyFallback />}>
                <LabFormatCalibrationPage />
              </React.Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="/lab-formats/:id"
          element={
            <AdminRoute>
              <LabFormatDetailPage />
            </AdminRoute>
          }
        />
        <Route path="/ai-usage" element={<Navigate to="/settings?tab=ai-usage" replace />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </SentryRoutes>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      </AppContainer>
    </StyledThemeProvider>
  );
};

function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  return (
    <ThemeProvider>
      <AuthProvider>
        <TimezoneProvider>
          <GoogleOAuthProvider clientId={googleClientId}>
            <Router>
              <AppContent />
            </Router>
          </GoogleOAuthProvider>
        </TimezoneProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;